import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { EmptyState, ErrorMessage, PageLoader } from '@/components/ui'
import { api } from '@/lib/api'
import { formatDateTime, formatNumber } from '@/lib/format'
import type { DatasetVersionListResponse, DatasetVersionSummary } from '@/lib/types'
import { useActiveWorkspace } from '@/stores/auth'

/**
 * История версий набора данных.
 *
 * Каждая загрузка файла создаёт новую версию, старые не удаляются: если в
 * очередной выгрузке из 1С обнаружится ошибка, должно быть видно, на каких
 * данных отчёт строился раньше.
 */
export function DatasetVersionsPage() {
  const { datasetId } = useParams<{ datasetId: string }>()
  const workspace = useActiveWorkspace()

  const versions = useQuery({
    queryKey: ['dataset-versions', datasetId],
    queryFn: () => api.get<DatasetVersionListResponse>(`/datasets/${datasetId}/versions`),
    enabled: Boolean(datasetId) && Boolean(workspace),
  })

  if (!workspace || versions.isLoading) return <PageLoader label="Загружаем историю версий…" />

  return (
    <div className="space-y-6">
      <ErrorMessage error={versions.error} />

      <section className="card overflow-hidden">
        <header className="border-b border-slate-100 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            История версий{versions.data ? ` · ${versions.data.total}` : ''}
          </h2>
        </header>

        {versions.data?.items.length === 0 && (
          <div className="p-6">
            <EmptyState
              title="Версий пока нет"
              description="Загрузите файл на странице источников."
            />
          </div>
        )}

        {versions.data && versions.data.items.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {versions.data.items.map((version) => (
              <VersionRow key={version.id} version={version} />
            ))}
          </ul>
        )}
      </section>

      <Link to="/sources" className="btn-ghost">
        ← Все источники данных
      </Link>
    </div>
  )
}

function VersionRow({ version }: { version: DatasetVersionSummary }) {
  const statusStyles: Record<DatasetVersionSummary['status'], string> = {
    pending: 'bg-slate-100 text-slate-600',
    processing: 'bg-brand-100 text-brand-800',
    ready: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-red-100 text-red-800',
  }
  const statusTitles: Record<DatasetVersionSummary['status'], string> = {
    pending: 'В очереди',
    processing: 'Обрабатывается',
    ready: 'Готова',
    failed: 'Ошибка',
  }

  return (
    <li className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-900">
          Версия {version.version_no} · {version.file_name}
        </p>
        <p className="tabular text-xs text-slate-500">
          {formatNumber(version.row_count)} строк · {version.column_count} колонок ·{' '}
          {formatDateTime(version.created_at)}
          {version.mapping_confirmed_at ? ' · сопоставление подтверждено' : ''}
        </p>
        {version.error_message && (
          <p className="mt-1 text-xs text-red-700">{version.error_message}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`badge ${statusStyles[version.status]}`}>
          {statusTitles[version.status]}
        </span>
        <Link to={`/dataset-versions/${version.id}`} className="btn-secondary py-1.5 text-xs">
          Открыть
        </Link>
      </div>
    </li>
  )
}
